import { useState, useRef, useCallback } from 'react'
import { Upload, X, Crop, Loader2, RefreshCw } from 'lucide-react'
import { toast } from '@/components/ui/sonner'
import ImageCropDialog from './ImageCropDialog'
import athleteApi from '@/lib/api'

export interface ImageUploadProps {
  label: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  shape?: 'rectangle' | 'round'
  usageType?: 'hero' | 'brand' | 'partner' | 'generic'
}

const ImageUpload = ({
  label,
  value,
  onChange,
  shape = 'rectangle',
  usageType = 'generic',
}: ImageUploadProps) => {
  const [isDragging, setIsDragging] = useState(false)
  const [cropDialogOpen, setCropDialogOpen] = useState(false)
  const [pendingImage, setPendingImage] = useState<string>('')
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file')
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB')
      return
    }

    const reader = new FileReader()
    reader.onload = (e) => {
      const result = e.target?.result as string
      setPendingImage(result)
      setCropDialogOpen(true)
    }
    reader.onerror = () => {
      toast.error('Failed to read image')
    }
    reader.readAsDataURL(file)
  }, [])

  const handleCropComplete = useCallback(
    async (croppedImage: string) => {
      setIsUploading(true)
      try {
        const response = await athleteApi.post('/athlete/profile/public/upload-image', {
          image: croppedImage,
          type: usageType,
        })

        if (response.data.success) {
          onChange(response.data.data.url)
          toast.success('Image uploaded successfully')
        }
      } catch (error) {
        console.error('Upload failed', error)
        toast.error('Failed to upload image')
      } finally {
        setIsUploading(false)
        setPendingImage('')
      }
    },
    [onChange, usageType],
  )

  const handleEditCrop = useCallback(() => {
    if (value) {
      setPendingImage(value)
      setCropDialogOpen(true)
    }
  }, [value])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)

      const file = e.dataTransfer.files[0]
      if (file) {
        handleFileSelect(file)
      }
    },
    [handleFileSelect],
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        handleFileSelect(file)
      }
    },
    [handleFileSelect],
  )

  const handleRemove = useCallback(() => {
    onChange('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [onChange])

  return (
    <div className="space-y-2">
      <label
        className="block text-sm text-gray-300 font-medium bg-transparent"
        style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}
      >
        {label}
      </label>

      {/* Always-present hidden file input so it's accessible from both states */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileInputChange}
        className="hidden"
        disabled={isUploading}
      />

      {value ? (
        // Preview State
        <div className="relative mt-2">
          <div
            className={`relative overflow-hidden bg-gray-800 border border-white/10 ${
              shape === 'round' ? 'w-24 h-24 rounded-full mx-auto' : 'rounded-xl'
            }`}
            style={shape !== 'round' ? { aspectRatio: '1/1' } : undefined}
          >
            <img
              src={value}
              alt="Preview"
              className={`w-full h-full object-cover object-center ${isUploading ? 'opacity-50' : ''}`}
            />

            {isUploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <Loader2 className="w-6 h-6 text-[#C0FD72] animate-spin" />
              </div>
            )}

            {!isUploading && (
              <>
                <button
                  type="button"
                  onClick={handleEditCrop}
                  className={`absolute p-1.5 bg-black/50 hover:bg-black/70 text-white transition-colors ${
                    shape === 'round' ? 'top-0 left-0 rounded-full' : 'top-2 left-2 rounded-full'
                  }`}
                  title="Adjust crop"
                >
                  <Crop className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className={`absolute p-1.5 bg-black/50 hover:bg-black/70 text-white transition-colors ${
                    shape === 'round'
                      ? 'bottom-0 left-0 rounded-full'
                      : 'top-2 left-10 rounded-full'
                  }`}
                  title="Replace image"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={handleRemove}
                  className={`absolute p-1.5 bg-black/50 hover:bg-black/70 text-white transition-colors ${
                    shape === 'round' ? 'top-0 right-0 rounded-full' : 'top-2 right-2 rounded-full'
                  }`}
                  title="Remove image"
                >
                  <X className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
      ) : (
        // Upload State
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !isUploading && fileInputRef.current?.click()}
          className={`
            relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all
            ${
              isDragging
                ? 'border-[#C0FD72] bg-[#C0FD72]/10'
                : 'border-white/10 hover:border-white/20 bg-white/5'
            }
          `}
        >
          {isUploading ? (
            <Loader2 className="w-8 h-8 mx-auto mb-2 text-[#C0FD72] animate-spin" />
          ) : (
            <Upload
              className={`w-8 h-8 mx-auto mb-2 ${isDragging ? 'text-[#C0FD72]' : 'text-gray-400'}`}
            />
          )}
          <p className="text-sm text-gray-300" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {isUploading
              ? 'Uploading...'
              : isDragging
                ? 'Drop image here'
                : 'Click or drag image here'}
          </p>
          <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Max 10MB · JPG, PNG, WebP
          </p>
        </div>
      )}

      {/* Crop Dialog */}
      <ImageCropDialog
        open={cropDialogOpen}
        onOpenChange={setCropDialogOpen}
        imageSrc={pendingImage}
        onCropComplete={handleCropComplete}
        shape={shape}
        aspectRatio={1}
      />
    </div>
  )
}

export default ImageUpload
